import CustomButton from 'Components/CustomButton'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DeleteButton from 'Components/DeleteButton'
import FormTextField from 'Components/FormTextField'
import StyledForm from 'Components/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import { WithLoader } from 'Components/WithLoader'
import {
  getPtaQueryParamOrSkip,
  useGetProductGroupParameterGroupQuery
} from 'Services/ProductManagement/Features/Parameter/parameterApi'
import { SELECTED_SALES_RULE_PARAMETER_KEY } from 'Services/ProductManagement/Features/SalesRule/Hooks/useSalesRulesWithParams'
import {
  useCreateSingleSalesRuleMutation,
  useUpdateSingleSalesRuleMutation
} from 'Services/ProductManagement/Features/SalesRule/salesRuleApi'
import type { SalesRule } from 'Services/ProductManagement/Features/SalesRule/types'
import { useGetSingleZoneDefinitionQuery } from 'Services/ProductManagement/Features/ZoneDefinition/zoneDefinitionApi'
import { useTypedSelector } from 'Store'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

interface SalesRuleFormProps {
  salesRule?: SalesRule
  successBaseUrl: string
}

export function SalesRuleForm({ salesRule, successBaseUrl }: Readonly<SalesRuleFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!salesRule
  const { user } = useTypedSelector(state => state.user)

  const { data: zoneDefinition, isLoading: isZoneDefinitionLoading } =
    useGetSingleZoneDefinitionQuery(user?.pta ?? '', {
      refetchOnMountOrArgChange: true,
      skip: !user?.pta
    })

  const { data: productGroupParameterGroup, isLoading: isProductGroupParameterGroupLoading } =
    useGetProductGroupParameterGroupQuery(getPtaQueryParamOrSkip(user?.pta), {
      refetchOnMountOrArgChange: true
    })
  const [createSingleSalesRule, { isLoading: isCreateLoading }] = useCreateSingleSalesRuleMutation()
  const [updateSingleSalesRule, { isLoading: isUpdateLoading }] = useUpdateSingleSalesRuleMutation()

  const [salesRuleDetails, setSalesRuleDetails] = useState<Partial<SalesRule>>(
    (isEdit && salesRule) || {}
  )

  const handleChange = (
    value: string | string[] | number | boolean | undefined,
    field: keyof SalesRule
  ) => {
    if (value !== undefined) {
      setSalesRuleDetails(prevState => ({ ...prevState, [field]: value }) as SalesRule)
    }
  }

  const handleZoneMapChange = (newValue: { value: string; label: string }[], index: number) => {
    setSalesRuleDetails(prevState => {
      const zoneMap = newValue.map(({ value }) => value)
      const zoneRequirements = [...(prevState.zone_requirements ?? [])]

      if (index < zoneRequirements.length) {
        // Update existing requirement
        zoneRequirements[index] = {
          ...zoneRequirements[index],
          zone_map: zoneMap
        }
      } else {
        // Add new requirement if index is out of bounds
        zoneRequirements.push({ ...zoneRequirements[index], zone_map: zoneMap })
      }

      return { ...prevState, zone_requirements: zoneRequirements }
    })
  }

  const handleExtensionAreaZoneMapChange = (
    value: { value: string; label: string }[],
    zoneRequirementIndex: number,
    extensionAreaIndex: number
  ) => {
    if (!value) return

    setSalesRuleDetails(prevState => {
      // Create a deep copy of zone_requirements
      const zoneRequirements = [...(prevState.zone_requirements ?? [])]

      // Check if the zone requirement exists at the index
      if (zoneRequirementIndex < zoneRequirements.length) {
        // Extract just the values from the input
        const newZoneMap = value.map(item => item.value)

        // Create a copy of the specific zone requirement
        const updatedZoneRequirement = {
          ...zoneRequirements[zoneRequirementIndex]
        }

        // Create a copy of the required_one_of_existing_products array
        const updatedRequiredProducts = [
          ...(updatedZoneRequirement.required_one_of_existing_products ?? [])
        ]

        // If the extension area exists, update it
        if (extensionAreaIndex < updatedRequiredProducts.length) {
          updatedRequiredProducts[extensionAreaIndex] = {
            ...updatedRequiredProducts[extensionAreaIndex],
            zone_map: newZoneMap
          }
        }

        // Update the zone requirement with the new required products
        updatedZoneRequirement.required_one_of_existing_products = updatedRequiredProducts

        // Update the specific zone requirement in the array
        zoneRequirements[zoneRequirementIndex] = updatedZoneRequirement
      }

      // Return the updated state
      return {
        ...prevState,
        zone_requirements: zoneRequirements
      }
    })
  }

  const handleProductGroupChange = (
    value: string,
    zoneRequirementIndex: number,
    extensionAreaIndex: number
  ) => {
    // Only process if value is defined
    if (value === undefined) return

    setSalesRuleDetails(prevState => {
      // Get current zone requirements array (or empty array if null)
      const zoneRequirements = prevState.zone_requirements ?? []
      const currentZoneRequirement = zoneRequirements[zoneRequirementIndex]

      // Update the product group for the specific extension area
      const updatedExtensionAreas = currentZoneRequirement.required_one_of_existing_products.map(
        (extensionArea, idx) =>
          idx === extensionAreaIndex ? { ...extensionArea, product_group: value } : extensionArea
      )

      // Create updated zone requirement with new extension areas
      const updatedZoneRequirement = {
        ...currentZoneRequirement,
        required_one_of_existing_products: updatedExtensionAreas
      }

      // Replace the specific zone requirement in the array
      const updatedZoneRequirements = zoneRequirements.map((requirement, idx) =>
        idx === zoneRequirementIndex ? updatedZoneRequirement : requirement
      )

      // Return updated state
      return {
        ...prevState,
        zone_requirements: updatedZoneRequirements
      }
    })
  }

  const addZoneRequirement = () => {
    setSalesRuleDetails(prevState => ({
      ...prevState,
      zone_requirements: [
        ...(prevState.zone_requirements ?? []),
        { required_one_of_existing_products: [{ zone_map: [], product_group: '' }], zone_map: [] }
      ]
    }))
  }

  const removeZoneRequirement = (index: number) => {
    setSalesRuleDetails(prevState => {
      const zoneRequirements = prevState.zone_requirements ?? []
      const updatedZoneRequirements = zoneRequirements.filter((_req, idx) => idx !== index)
      return { ...prevState, zone_requirements: updatedZoneRequirements }
    })
  }

  const addExtensionArea = (zoneRequirementIndex: number) => {
    setSalesRuleDetails(prevState => {
      const zoneRequirements = prevState.zone_requirements ?? []
      const zoneRequirement = zoneRequirements[zoneRequirementIndex]

      const updatedZoneRequirement = {
        ...zoneRequirement,
        required_one_of_existing_products: [
          ...zoneRequirement.required_one_of_existing_products,
          { zone_map: [], product_group: '' }
        ]
      }

      const updatedZoneRequirements = zoneRequirements.map((req, idx) =>
        idx === zoneRequirementIndex ? updatedZoneRequirement : req
      )

      return { ...prevState, zone_requirements: updatedZoneRequirements }
    })
  }

  const removeExtensionArea = (zoneRequirementIndex: number, extensionAreaIndex: number) => {
    setSalesRuleDetails(prevState => {
      const zoneRequirements = prevState.zone_requirements ?? []
      const zoneRequirement = zoneRequirements[zoneRequirementIndex]

      const updatedZoneRequirement = {
        ...zoneRequirement,
        required_one_of_existing_products: zoneRequirement.required_one_of_existing_products.filter(
          (_req, idx) => idx !== extensionAreaIndex
        )
      }

      const updatedZoneRequirements = zoneRequirements.map((req, idx) =>
        idx === zoneRequirementIndex ? updatedZoneRequirement : req
      )

      return { ...prevState, zone_requirements: updatedZoneRequirements }
    })
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (salesRule) {
      updateSingleSalesRule({
        id: salesRule.id,
        ...salesRuleDetails
      })
        .unwrap()
        .then(() => {
          SnackbarUtils.success(t`Sales rule successfully updated.`)
          navigate(successBaseUrl)
        })
        .catch(() => {
          SnackbarUtils.error(t`There was an error updating the sales rule.`)
        })
      return
    }

    createSingleSalesRule(salesRuleDetails)
      .unwrap()
      .then((salesRuleResponse: SalesRule) => {
        SnackbarUtils.success(t`Sales rule successfully created.`)
        navigate(
          appendQueryParamsToUrl(successBaseUrl, {
            [SELECTED_SALES_RULE_PARAMETER_KEY]: salesRuleResponse.id
          })
        )
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error creating the sales rule.
        `)
      })
  }

  const zoneOptions = zoneDefinition
    ? Object.entries(zoneDefinition.translations).map(([value, translations]) => ({
        value,
        label: getTranslation(translations, 'fi').value
      }))
    : []

  const productGroupOptions =
    productGroupParameterGroup?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []

  const isLoading = isProductGroupParameterGroupLoading || isZoneDefinitionLoading

  const isSubmitLoading = isCreateLoading || isUpdateLoading

  return (
    <WithLoader loading={isLoading}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
            <Box>
              <FormTextField
                sx={{ minWidth: 200 }}
                name="name"
                required
                label={t`Name`}
                value={salesRuleDetails.name ?? ''}
                onChange={e => handleChange(e.target.value, 'name')}
              />
            </Box>
            <Box sx={{ maxWidth: 1085, mb: 2 }}>
              <StyledLabel>{t`Zone requirements`}</StyledLabel>
              {salesRuleDetails.zone_requirements?.map((zoneRequirement, index) => (
                <Paper key={`${salesRuleDetails.id}-${index}`} sx={{ px: 3 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <FormControl>
                      <Autocomplete
                        size="small"
                        multiple
                        options={zoneOptions}
                        getOptionLabel={option => option.label}
                        defaultValue={zoneRequirement.zone_map.map(zone => ({
                          value: zone,
                          label: getTranslation(zoneDefinition?.translations[zone], 'fi').value
                        }))}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        renderInput={params => (
                          <TextField {...params} label={t`Select zone codes`} />
                        )}
                        onChange={(_, newValue) => handleZoneMapChange(newValue, index)}
                        disableCloseOnSelect={true}
                        sx={{ width: 300 }}
                      />
                    </FormControl>
                    <DeleteButton onClick={() => removeZoneRequirement(index)} size="small">
                      {t`Delete`}
                    </DeleteButton>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                    >{t`Required zones`}</Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2
                      }}
                    >
                      {zoneRequirement.required_one_of_existing_products.map(
                        (extensionArea, extensionAreaIndex) => (
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 2,
                              alignItems: 'center'
                            }}
                            key={`${extensionArea.product_group}-${index}`}
                          >
                            <FormControl required>
                              <InputLabel size="small">{t`Product group`}</InputLabel>
                              <Select
                                name="product_group"
                                required
                                size="small"
                                label={t`Product group`}
                                value={extensionArea?.product_group}
                                onChange={e =>
                                  handleProductGroupChange(
                                    e.target.value,
                                    index,
                                    extensionAreaIndex
                                  )
                                }
                                sx={{
                                  width: 300
                                }}
                              >
                                {productGroupOptions.map(({ label, value }) => (
                                  <MenuItem key={value} value={value}>
                                    {label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <FormControl>
                              <Autocomplete
                                size="small"
                                multiple
                                options={zoneOptions}
                                getOptionLabel={option => option.label}
                                defaultValue={extensionArea.zone_map.map(zone => ({
                                  value: zone,
                                  label: getTranslation(zoneDefinition?.translations[zone], 'fi')
                                    .value
                                }))}
                                isOptionEqualToValue={(option, value) =>
                                  option.value === value.value
                                }
                                renderInput={params => (
                                  <TextField {...params} label={t`Select zone codes`} />
                                )}
                                onChange={(_, newValue) =>
                                  handleExtensionAreaZoneMapChange(
                                    newValue,
                                    index,
                                    extensionAreaIndex
                                  )
                                }
                                disableCloseOnSelect={true}
                                sx={{
                                  width: 300
                                }}
                              />
                            </FormControl>
                            <CustomButton
                              onClick={() => removeExtensionArea(index, extensionAreaIndex)}
                              size="small"
                              variant="text"
                              color="error"
                              startIcon={<CloseIcon />}
                            >
                              {t`Delete row`}
                            </CustomButton>
                          </Box>
                        )
                      )}
                    </Box>
                    <CustomButton
                      onClick={() => addExtensionArea(index)}
                      startIcon={<AddIcon />}
                      variant="text"
                      size="small"
                      sx={{ mb: 2 }}
                    >{t`Add required zone`}</CustomButton>
                  </Box>
                </Paper>
              ))}
              <CustomButton
                onClick={addZoneRequirement}
                startIcon={<AddIcon />}
                size="small"
                sx={{ mt: 2 }}
              >{t`Add zone requirement`}</CustomButton>
            </Box>
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              type="submit"
              size="large"
              iconPosition="end"
            >
              {t`Save sales rule`}
            </CustomLoadingButton>
          </StyledForm>
        </Grid>
      </Grid>
    </WithLoader>
  )
}
